// Quiz Details Component - Shows quiz information before starting
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  FileText,
  Award,
  Users,
  Calendar,
  CheckCircle,
  Play,
  Eye,
} from "lucide-react";
import { Quiz, QuizAttempt } from "../../../types/quiz";
import quizService from "../../../services/quizService";
import AvailabilityStatus from "./AvailabilityStatus";
import LoadingSpinner from "../../common/LoadingSpinner";

interface QuizDetailsProps {
  quizId?: string; // Optional - can get from URL params
  onStartQuiz?: () => void;
  onViewResults?: () => void; // For inline usage
  inline?: boolean; // Whether this is shown inline or on a separate page
  courseId?: string; // For inline usage
}

const QuizDetails: React.FC<QuizDetailsProps> = ({
  quizId: propQuizId,
  onStartQuiz,
  onViewResults,
  inline = false,
  courseId,
}) => {
  const { quizId: paramQuizId } = useParams<{ quizId: string }>();
  const quizId = propQuizId || paramQuizId;

  const navigate = useNavigate();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (quizId) {
      loadQuizDetails();
    }
  }, [quizId]);

  const loadQuizDetails = async () => {
    try {
      setLoading(true);

      const [quizData, attemptsData] = await Promise.all([
        quizService.getQuizForStudent(quizId),
        quizService.getStudentAttempts(quizId).catch((error) => {
          console.warn(
            "Failed to load attempts, continuing without them:",
            error
          );
          return []; // Return empty array if attempts fail to load
        }),
      ]);

      setQuiz(quizData);
      setAttempts(attemptsData);
    } catch (error: any) {
      console.error("Error loading quiz details:", error);
      if (
        error.message?.includes("Quiz not found") ||
        error.message?.includes("Route not found")
      ) {
        // Quiz doesn't exist, show error state
        setQuiz(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = async () => {
    if (onStartQuiz) {
      onStartQuiz();
    } else {
      setStarting(true);
      try {
        // Navigate to quiz taking page
        navigate(`/student/quizzes/${quizId}/take`);
      } catch (error) {
        console.error("Error starting quiz:", error);
        setStarting(false);
      }
    }
  };

  const getBestAttempt = () => {
    if (attempts.length === 0) return null;
    return attempts.reduce((best, current) => {
      if (!best) return current;
      return (current.score || 0) > (best.score || 0) ? current : best;
    });
  };

  const getLatestAttempt = () => {
    if (attempts.length === 0) return null;
    return attempts.sort(
      (a, b) =>
        new Date(b.completedAt || b.submittedAt || 0).getTime() -
        new Date(a.completedAt || a.submittedAt || 0).getTime()
    )[0];
  };

  const getSubmittedAttempts = () => {
    return attempts.filter(
      (a) =>
        a.status === "submitted" ||
        a.status === "submitted_pending_grading" ||
        a.status === "partially_graded" ||
        a.status === "timed_out"
    );
  };

  if (!quizId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No quiz ID provided</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Quiz Not Found
          </h3>
          <p className="text-red-700 mb-4">
            The quiz you're looking for doesn't exist or has been removed.
          </p>
          {inline && courseId && (
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Go Back to Course
            </button>
          )}
        </div>
      </div>
    );
  }

  const bestAttempt = getBestAttempt();
  const latestAttempt = getLatestAttempt();
  const submittedAttempts = getSubmittedAttempts();
  const canRetake = quiz.attempts ? attempts.length < quiz.attempts : true;
  const hasPassed = bestAttempt && bestAttempt.score && bestAttempt.score >= 60;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Quiz Header */}
      <div className="bg-white rounded-lg border shadow-sm mb-6">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {quiz.title}
              </h1>
              {quiz.description && (
                <p className="text-gray-600">{quiz.description}</p>
              )}
            </div>
            <AvailabilityStatus quiz={quiz} />
          </div>

          {/* Quiz Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-gray-600">
                {quiz.questions?.length || 0} Questions
              </span>
            </div>
            {quiz.timeLimit && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-gray-600">
                  {quiz.timeLimit} minutes
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-gray-600">
                Passing: {quiz.passingScore || 60}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-gray-600">
                {quiz.attempts || "Unlimited"} attempts
              </span>
            </div>
          </div>

          {/* Instructions */}
          {quiz.instructions && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">
                Instructions
              </h3>
              <p className="text-sm text-blue-800">{quiz.instructions}</p>
            </div>
          )}
        </div>
      </div>

      {/* Previous Attempts */}
      {attempts.length > 0 && (
        <div className="bg-white rounded-lg border shadow-sm mb-6">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Your Attempts
            </h2>
            <div className="space-y-3">
              {attempts.slice(0, 3).map((attempt, index) => (
                <div
                  key={attempt._id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {attempt.status === "completed" &&
                      attempt.score &&
                      attempt.score >= 60 ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <div className="w-4 h-4 rounded-full bg-gray-300" />
                      )}
                      <span className="text-sm font-medium">
                        Attempt {attempts.length - index}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {new Date(
                        attempt.completedAt ||
                          attempt.submittedAt ||
                          attempt.startedAt
                      ).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-right">
                    {attempt.score !== undefined ? (
                      <span
                        className={`text-sm font-medium ${
                          attempt.score >= 60
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {attempt.score.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-600">
                        {attempt.status === "completed"
                          ? "Completed"
                          : "In Progress"}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              {latestAttempt && (
                <div className="text-sm text-gray-600">
                  Last attempt:{" "}
                  {new Date(
                    latestAttempt.completedAt ||
                      latestAttempt.submittedAt ||
                      latestAttempt.startedAt
                  ).toLocaleDateString()}
                  {latestAttempt.score !== undefined && (
                    <span
                      className={`ml-2 font-medium ${
                        latestAttempt.score >= 60
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      ({latestAttempt.score.toFixed(1)}%)
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              {submittedAttempts.length > 0 && (
                <button
                  onClick={() => {
                    if (inline && onViewResults) {
                      onViewResults();
                    } else {
                      navigate(`/student/quizzes/${quizId}/results`);
                    }
                  }}
                  className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Review Results
                </button>
              )}
              <button
                onClick={handleStartQuiz}
                disabled={starting || !canRetake}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                {starting
                  ? "Starting..."
                  : canRetake
                  ? submittedAttempts.length > 0
                    ? "Retake Quiz"
                    : "Start Quiz"
                  : "Max Attempts Reached"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizDetails;
